import { useEffect, useState } from "react";
import { demoSessionLiveView } from "src/services/sessionLiveView/demoSessionLiveView";
import { presentSessionLiveView, type PresentedSessionLiveView } from "src/services/sessionLiveView/presentSessionLiveView";
import type { SessionLiveViewGateway } from "src/services/sessionLiveView/types";

type LoadStatus = "loading" | "ready" | "error";

export function useSessionLiveView(gateway: SessionLiveViewGateway = demoSessionLiveView) {
  const [view, setView] = useState<PresentedSessionLiveView | null>(null);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [assigningTutorId, setAssigningTutorId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    gateway
      .fetchSnapshot()
      .then((snapshot) => {
        if (cancelled) return;
        const presented = presentSessionLiveView(snapshot);
        setView(presented);
        setStatus("ready");
        const firstOpen = presented.tutorMissingSessions.find((session) => !session.assignedTutorName);
        setExpandedSessionId(firstOpen?.id ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("The live view did not load. Refresh the page.");
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [gateway]);

  const toggleQuickSub = (sessionId: string) => {
    setActionError(null);
    setExpandedSessionId((current) => (current === sessionId ? null : sessionId));
  };

  const assignSubstitute = async (sessionId: string, tutorId: string) => {
    setAssigningTutorId(tutorId);
    setActionError(null);
    try {
      const snapshot = await gateway.assignSubstitute(sessionId, tutorId);
      setView(presentSessionLiveView(snapshot));
      setExpandedSessionId(null);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "The substitute was not assigned. Try again.");
    } finally {
      setAssigningTutorId(null);
    }
  };

  return {
    view,
    status,
    loadError,
    actionError,
    expandedSessionId,
    assigningTutorId,
    toggleQuickSub,
    assignSubstitute,
  };
}
